<script setup lang="ts">
import {IconName, IconSize, odsIcon} from '../odsIcon';
import {computed, inject, nextTick, onMounted, PropType, reactive, ref, useAttrs, useSlots} from 'vue';

const emit = defineEmits(['mouseover', 'mouseout', 'blur', 'click']);
const RouterLinkComponent = inject('RouterLinkComponent');

const slots = useSlots()

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false
  },
  label: {
    type: String,
    default: ''
  },
  icon: {
    type: String as PropType<typeof IconName[keyof typeof IconName] | String>,
    default: ''
  },
  to: {
    type: Object,
    default: null
  },
  href: {
    type: String,
    default: ''
  },
  target: {
    type: String,
    default: "_self"
  },
  readonly: {
    type: Boolean,
    default: false
  },
  group: {
    type: Boolean,
    default: false,
    validator(value, props){
      if(value && (props.to || props.href)){
        console.error('NavigationItem with "group" option set to true cannot have "to" or "href" option')
        return false
      }
      return true
    }
  }
})

const submenuOpen = ref(false)
const subitems = ref()

const nbChildren = computed(() => {
  if(subitems.value)
    return subitems.value.children.length

  return 0
})



const linkAttributes: any = {}

if (props.to !== null) {
  linkAttributes.to = props.to;
} else if (props.href !== '') {
  linkAttributes.href = props.href;
  linkAttributes.target = props.target;
} else {
  linkAttributes.onClick = (event: Event) => clickButton(event);
  linkAttributes.onBlur = (event: Event) => blurButton(event);
  linkAttributes.onMouseover = (event: Event) => mouseover(event);
  linkAttributes.onMouseout = (event: Event) => mouseout(event);

}

const attrs = reactive(
    {
      ...useAttrs(),
      ...linkAttributes,
    }
)


const componentType = computed(() => {
  if (props.to !== null)
    return RouterLinkComponent;

  if (props.href !== '')
    return 'a';

  return 'div';

})

function mouseover(event: Event) {
  emit('mouseover', event);
}

function mouseout(event: Event) {
  emit('mouseout', event);
}

function blurButton(event: Event) {
  emit('blur', event);
}

function clickButton(event: Event) {
  nextTick(() => {
    if (props.disabled || props.readonly)
      return;

    emit('click', event);
  });
}

function toggleSubMenu(){
  if(submenuOpen.value){
    closeSubMenu()
  }
  else{
    openSubMenu()
  }
}

function openSubMenu(){
  submenuOpen.value = true
}

function closeSubMenu(){
  submenuOpen.value = false
}
</script>

<template>
  <template v-if="group">
    <div class="ods-navigation-group">
      <component :is="componentType"
                class="ods-navigation-item"
                :class="{disabled,readonly}"
                v-bind="attrs"
                @click="toggleSubMenu">
        <slot name="icon">
          <odsIcon v-if="icon" :name="icon" :size="IconSize.Medium"/>
        </slot>
        <span v-html="label"></span>
        <odsIcon :name="'keyboard_arrow_down'" class="icon-group" :class="{reverse: submenuOpen}" :size="IconSize.Medium"/>
      </component>
      <div class="ods-navigation-group-subitems-container" :class="{open: submenuOpen}" :style="{maxHeight: `${submenuOpen ? (nbChildren * 50) : 0}px`}">
        <div class="ods-navigation-group-lines">
          <div class="vertical-line"></div>
          <div class="arc-line" :style="{top: `${((n-1) * 2.5) + 1.2}rem`}" v-for="n in nbChildren"></div>
        </div>
        <div class="ods-navigation-group-subitems" ref="subitems">
          <slot></slot>
        </div>
      </div>
    </div>
  </template>
  <template v-else>
    <component :is="componentType"
              class="ods-navigation-item"
              :class="{disabled,readonly}"
              v-bind="attrs">
      <slot name="icon">
        <odsIcon v-if="icon" :name="icon" :size="IconSize.Medium"/>
      </slot>
      <span v-html="label"></span>
    </component>
  </template>
</template>

<style lang="scss">
</style>